import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function PrivacyPolicy() {
  const [settings, setSettings] = useState({});

  useEffect(() => {
    document.title = "Privacy Policy | Munmun Ladies Beauty Parlour";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "Privacy Policy for Munmun Ladies Beauty Parlour, Bankra, Howrah. Learn how we collect, use and protect your personal information when you book beauty appointments.");
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const updated = "June 2026";

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="border-b border-[#D4AF37]/25 bg-[#FDFBF7]/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-baseline gap-2" data-testid="privacy-logo">
            <span className="font-display text-2xl text-[#2A1B1D]">Munmun</span>
            <span className="hidden sm:inline text-[10px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Beauty Parlour</span>
          </a>
          <a href="/" data-testid="privacy-back-home" className="inline-flex items-center gap-2 text-sm text-[#5C4A4D] hover:text-[#8E3B46] transition-colors">
            <ArrowLeft size={16} /> Back to website
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Legal</p>
        <h1 className="font-display font-light text-5xl text-[#2A1B1D] mt-3">Privacy Policy</h1>
        <p className="text-sm text-[#5C4A4D] mt-3">Last updated: {updated}</p>

        <div className="mt-10 space-y-10 text-[#5C4A4D] leading-relaxed">
          <Section title="1. Introduction">
            <p>
              Munmun Ladies Beauty Parlour ("we", "us", "our") respects your privacy and is committed to
              protecting the personal information you share with us. This Privacy Policy explains what
              information we collect, how we use it, and the choices you have.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>We collect only the information needed to serve you well, including:</p>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>Your name and contact details (phone number, email) when you book an appointment or contact us.</li>
              <li>Appointment details such as the service selected, preferred date and time, and any notes you provide.</li>
              <li>Feedback or reviews you choose to submit.</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="space-y-2 list-disc pl-5">
              <li>To schedule, confirm, reschedule or cancel your appointments.</li>
              <li>To contact you regarding your bookings, offers or enquiries (including via phone or WhatsApp).</li>
              <li>To improve our services and your experience at the parlour.</li>
            </ul>
          </Section>

          <Section title="4. Sharing of Information">
            <p>
              We do not sell or rent your personal information. We may share limited information with trusted
              service providers (for example, payment or messaging providers) strictly to deliver the services
              you request, or where required by law.
            </p>
          </Section>

          <Section title="5. Data Security">
            <p>
              We take reasonable technical and organisational measures to protect your information against
              unauthorised access, loss or misuse. Access to booking data is protected and limited to authorised
              staff only.
            </p>
          </Section>

          <Section title="6. Your Rights">
            <p>
              You may request access to, correction of, or deletion of your personal information at any time by
              contacting us using the details below. You may also opt out of promotional messages at any time.
            </p>
          </Section>

          <Section title="7. Cookies & Analytics">
            <p>
              Our website may use minimal cookies or similar technologies to keep the site working smoothly and
              to understand general usage. You can control cookies through your browser settings.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              Our services are intended for adults. We do not knowingly collect personal information from
              children without appropriate consent.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with a
              revised "Last updated" date.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>If you have any questions about this Privacy Policy or your data, please contact us:</p>
            <div className="mt-4 rounded-2xl bg-[#F4EFEA] p-6 text-[#2A1B1D]">
              <p className="font-display text-2xl">{settings.name || "Munmun Ladies Beauty Parlour"}</p>
              <p className="mt-2 text-sm">{settings.address || "Saratpally, Opposite Radha Govind Mandir, Bankra, Howrah, West Bengal 711403"}</p>
              <p className="mt-2 text-sm">Phone: {settings.phone || "+91 82400 37030"}</p>
              {settings.email && <p className="text-sm">Email: {settings.email}</p>}
            </div>
          </Section>
        </div>

        <div className="mt-16 pt-8 border-t border-[#2A1B1D]/10 text-xs text-[#5C4A4D]">
          © {new Date().getFullYear()} {settings.name || "Munmun Ladies Beauty Parlour"}. All rights reserved.
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl text-[#2A1B1D] mb-3">{title}</h2>
      {children}
    </section>
  );
}
